import { UserType } from "@/types/AuthTypes";
import { PermissionType } from "@/types/PermissionTypes";
import { useAuth } from "@/context/AuthContext";

type PermissionCheckOptions = {
  strict?: boolean;
};

// 2nd param undefined means checking only if all permissions are allowed
const isPermitted = (
  user: UserType,
  permission: string | undefined,
  allPermissions: PermissionType[],
  options: PermissionCheckOptions = { strict: false }
) => {
  if (permission) {
    const userOverrideDenied = user?.sf_user_permissions?.some((sf_permission) => {
      const isDenied =
        sf_permission.sf_rel_user_permissions?.is_denied ?? false;
      return sf_permission.value === permission && isDenied === true;
    });
    if (userOverrideDenied) {
      return false;
    }
  }

  if (permission) {
    const userOverrideHasPermission = user?.sf_user_permissions?.some(
      (sf_permission) => {
        const isDenied =
          sf_permission.sf_rel_user_permissions?.is_denied ?? false;
        return sf_permission.value === permission && isDenied !== true;
      }
    );
    if (userOverrideHasPermission) {
      return true;
    }
  }

  // is all permission allowed, if strict is false
  if (options.strict == false && user?.sf_role?.has_all_permissions == true) {
    if (permission) {
      // Check if user explicitly has this permission
      const userHasPermission = user?.sf_role?.sf_permissions?.some(
        (sf_permission) => sf_permission.value === permission
      );

      if (userHasPermission) {
        return true;
      }

      // Check if this permission requires skipping for super admins
      const permissionDefinition = allPermissions.find(
        (p) => p.value === permission
      );

      if (permissionDefinition?.skip_has_all_permissions === true) {
        // Permission skips has_all_permissions and user doesn't have it explicitly
        return false;
      }

      // Permission doesn't skip → user has it via has_all_permissions
      return true;
    }
    return true;
  }

  // is the permission allowed for the user
  if (permission) {
    return user?.sf_role?.sf_permissions?.some(
      (sf_permission) => sf_permission.value === permission
    );
  }

  return false;
};

// Hook version that gets allPermissions from context
export const useIsPermitted = () => {
  const { user, allPermissions } = useAuth();

  return (permission?: string, options?: PermissionCheckOptions) => {
    if (!user) return false;
    return isPermitted(user, permission, allPermissions, options);
  };
};

export default isPermitted;
